import { Badge, OptionCard, RadioGroup } from '@/components/ui';
import type { OptionPriceOut } from '@/lib/api/types';
import { formatPaise, formatSheets } from '@/lib/format';

import { cheapestSlug } from './presets';

export interface PresetPickerProps {
  options: OptionPriceOut[];
  /** Slug of the selected preset. */
  value: string | null;
  onChange: (slug: string) => void;
  /** True while a new set of prices is in flight; dims the numbers, keeps the layout. */
  refreshing?: boolean;
}

/**
 * The preset list, with a live total on every card.
 *
 * The reason each card carries its OWN total rather than a "+₹20" delta against
 * the selection: the brief's actual question is "is double-sided cheaper?", and
 * a delta only answers that relative to whatever happens to be selected. Four
 * absolute delivered totals answer it at a glance, in any order, and they are
 * the same integers the server will charge.
 *
 * Sheets are on the card too, because that is the physical fact behind the
 * saving — "5 sheets" next to "10 sheets" explains the price difference better
 * than the price difference does.
 */
export function PresetPicker({ options, value, onChange, refreshing = false }: PresetPickerProps) {
  const cheapest = cheapestSlug(options);

  return (
    <RadioGroup
      legend="How should we print it?"
      hint="Prices include printing, handling and tax. Delivery is added once you enter a PIN code."
    >
      {options.map((entry) => {
        const { option, totals, document_size_label: fitFrom } = entry;
        const isCheapest = option.slug === cheapest && options.length > 1;

        return (
          <OptionCard
            key={option.id}
            name="print-preset"
            value={option.slug}
            checked={value === option.slug}
            onChange={() => onChange(option.slug)}
            label={
              <span className="flex flex-wrap items-center gap-2">
                {option.label}
                {isCheapest ? (
                  <Badge tone="success" srPrefix="This option is">
                    Cheapest
                  </Badge>
                ) : null}
              </span>
            }
            description={
              <>
                {formatSheets(totals.sheets)} &middot; {formatPaise(option.price_per_page_paise)}{' '}
                per page
                {option.description ? <span className="block">{option.description}</span> : null}
                {/* On the card as well as in the notice above, because the
                    cards can differ: if one preset stocks the customer's size
                    and another does not, a single notice at the top cannot say
                    which is which. Kept to three words — the notice carries the
                    explanation, this only says which paper THIS preset uses. */}
                {fitFrom ? <span className="block">Printed on {entry.size_label}.</span> : null}
              </>
            }
            meta={
              <span className={refreshing ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
                {formatPaise(totals.total_paise)}
              </span>
            }
          />
        );
      })}
    </RadioGroup>
  );
}
